import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useProfile } from '@/hooks/useProfile.ts'
import { useActivityLog } from '@/hooks/useActivityLog.ts'
import { ProfileForm } from '@/features/profile/components/ProfileForm.tsx'

export function BeneficiaryOnboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { profile, loading, fetchProfile, saveProfile } = useProfile()
  const { logActivity } = useActivityLog()
  const [checked, setChecked] = useState(false)
  const initialized = useRef(false)

  const initialize = useCallback(async () => {
    if (initialized.current) return
    initialized.current = true
    await fetchProfile()
    setChecked(true)
  }, [fetchProfile])

  useEffect(() => {
    const id = setTimeout(() => initialize(), 0)
    return () => clearTimeout(id)
  }, [initialize])

  useEffect(() => {
    if (!checked) return
    if (profile) {
      navigate('/dashboard', { replace: true })
    }
  }, [checked, profile, navigate])

  const handleProfileSubmit = async (data: {
    email: string
    name?: string
    phone?: string
  }) => {
    const result = await saveProfile(data)
    if (result) {
      await logActivity('profile_updated')
      navigate('/dashboard', { replace: true })
    }
  }

  if (!checked || loading) {
    return (
      <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
        <div className="text-fg-muted flex min-h-[300px] items-center justify-center text-[0.9375rem]">
          {t('common.loading')}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
      <h1 className="text-fg mb-1 text-[1.75rem] font-bold max-md:text-[1.375rem]">
        {t('onboarding.beneficiaryTitle')}
      </h1>
      <p className="text-fg-secondary mb-12">
        {t('onboarding.beneficiarySubtitle')}
      </p>

      <ProfileForm
        profile={profile}
        onSubmit={handleProfileSubmit}
        submitLabel={t('onboarding.continueToDashboard')}
      />
    </div>
  )
}
